import { object, string, number, boolean } from 'yup';
import type { TravelOfferDiag, TravelOfferDiagOtaUrls, TravelOfferDiagSyntheticStrikeout } from './TravelOfferDiag.types';
import { addAction, addError } from '@shared/utils/logger';
import { ensureHttpsPrefix, isPublicWebUrl } from '@shared/utils/urlHelpers';

/** GraphQL `JSON` scalars often deserialize numbers/booleans as non-strings; coerce for diagnostics validation. */
const stringTransform = (value: string, original: unknown) => (original !== null && original !== undefined && typeof original !== 'string' ? String(original) : value);

const numberTransform = (value: number, original: unknown) => {
  if (typeof original === 'string' && original.trim() !== '') {
    const n = Number(original);
    return Number.isFinite(n) ? n : value;
  }
  return value;
};

const diagStringRequired = () => string().transform(stringTransform).required();
const diagStringOptional = () => string().transform(stringTransform);
const diagNumberRequired = () => number().transform(numberTransform).required();
const diagNumberOptional = () => number().transform(numberTransform).notRequired();

const booleanTransform = (value: boolean, original: unknown) => {
  if (original === 'true') return true;
  if (original === 'false') return false;
  return value;
};

const diagBooleanRequired = () => boolean().transform(booleanTransform).required();
const diagBooleanOptional = () => boolean().transform(booleanTransform).notRequired();

const travelOfferDiagOfferSchema = object({
  base: diagStringRequired(),
  total: diagStringRequired(),
  provider: diagStringRequired(),
  nightlyBase: diagStringRequired(),
  nightlyTotal: diagStringRequired(),
  supplierSource: diagStringRequired(),
  perOccupancyNightlyTotal: diagStringRequired(),
  perOccupancyNightlyBase: diagStringRequired(),
  originalBase: diagStringOptional(),
  originalTotal: diagStringOptional(),
  nightlyOriginalBase: diagStringOptional(),
  nightlyOriginalTotal: diagStringOptional(),
  perOccupancyNightlyOriginalTotal: diagStringOptional(),
  perOccupancyNightlyOriginalBase: diagStringOptional(),
});

const travelOfferDiagMarginSchema = object({
  spread: diagStringRequired(),
  markup: diagStringRequired(),
  markupNightly: diagStringRequired(),
  markupPercentOfSpread: diagNumberRequired(),
  markupPerOccupancyNightly: diagStringRequired(),
});

const travelOfferDiagOfferMarginSchema = object({
  distributionMode: diagStringRequired(),
  isWinnerOffer: diagBooleanRequired(),
  markup: diagStringRequired(),
  markupNightly: diagStringRequired(),
  markupPerOccupancyNightly: diagStringRequired(),
  postMarkupBase: diagStringRequired(),
  postMarkupTotal: diagStringRequired(),
  preMarkupBase: diagStringRequired(),
  preMarkupTotal: diagStringRequired(),
});

const travelOfferDiagSpreadAdjustmentSchema = object({
  bestPublishedPrice: diagStringOptional(),
  bestPublishedPriceNightly: diagStringOptional(),
  bestPublishedPriceProvider: diagStringOptional(),
  effectiveCompetitorTotal: diagStringOptional(),
  effectiveCompetitorNightly: diagStringOptional(),
  rawSpread: diagStringRequired(),
  spreadCap: diagStringRequired(),
  finalSpread: diagStringRequired(),
  markupPercentOfSpread: diagNumberOptional(),
  usedPublishedPriceReference: diagBooleanRequired(),
  spreadWasCapped: diagBooleanRequired(),
  maxSpreadPercentUsed: diagNumberRequired(),
});

const travelOfferDiagOtaUrlsSchema = object({
  booking: diagStringOptional().nullable().notRequired(),
  expedia: diagStringOptional().nullable().notRequired(),
});

const travelOfferDiagSyntheticStrikeoutSchema = object({
  applied: diagBooleanRequired(),
  discountPercent: diagNumberRequired(),
  surface: diagStringOptional(),
  skipReason: diagStringOptional(),
  impliedPercent: diagNumberOptional(),
  anchorPrice: diagStringOptional(),
  anchorStrikeout: diagStringOptional(),
  ratio: diagNumberOptional(),
  roundedToMajorUnit: diagBooleanOptional(),
});

// Every margin field is optional (TRAV-3970): a hotel priced by synthetic strikeout alone sends a blob
// with no margin sections at all, and requiring them here would reject it outright and leave the panel
// blank. `syntheticStrikeout` has no default, so a margin-only payload parses to exactly what it did
// before this section existed.
const travelOfferDiagSchema = object({
  nights: diagNumberOptional(),
  currency: diagStringOptional(),
  occupancy: diagNumberOptional(),
  retailTotal: diagStringOptional(),
  algorithmVersion: diagStringOptional(),
  eligibilityReason: diagStringOptional(),
  margin: travelOfferDiagMarginSchema.notRequired().default(undefined),
  winner: travelOfferDiagOfferSchema.nullable().default(null),
  secondBest: travelOfferDiagOfferSchema.nullable().default(null),
  spreadAdjustment: travelOfferDiagSpreadAdjustmentSchema.nullable().notRequired().default(null),
  offerMargin: travelOfferDiagOfferMarginSchema.nullable().notRequired().default(null),
  otaUrls: travelOfferDiagOtaUrlsSchema.nullable().notRequired(),
});

const isRecord = (data: unknown): data is Record<string, unknown> => typeof data === 'object' && data !== null && !Array.isArray(data);

const normalizeOtaUrl = (value: unknown): string | null | undefined => {
  if (typeof value === 'string') {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return undefined;
    }

    return isPublicWebUrl(ensureHttpsPrefix(trimmedValue)) ? trimmedValue : undefined;
  }

  if (value === null) {
    return null;
  }

  return undefined;
};

const normalizeOtaUrls = (value: unknown): TravelOfferDiagOtaUrls | null | undefined => {
  if (value === null) {
    return null;
  }

  if (!isRecord(value)) {
    return undefined;
  }

  const booking = normalizeOtaUrl(value.booking);
  const expedia = normalizeOtaUrl(value.expedia);

  if (booking === undefined && expedia === undefined) {
    return undefined;
  }

  return {
    ...(booking !== undefined ? { booking } : {}),
    ...(expedia !== undefined ? { expedia } : {}),
  };
};

/**
 * Validated on its own rather than as a field of the root schema, and stripped from the root input below,
 * so a malformed section is dropped instead of failing the whole payload. A single bad `ratio` is enough
 * to make yup reject an object, and that must not cost a superadmin the rest of the panel.
 */
const parseSyntheticStrikeout = (value: unknown): TravelOfferDiagSyntheticStrikeout | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  try {
    return travelOfferDiagSyntheticStrikeoutSchema.validateSync(value, {
      abortEarly: false,
      strict: false,
    }) as TravelOfferDiagSyntheticStrikeout;
  } catch (error) {
    addAction('travelOfferDiagSyntheticStrikeoutDropped', { severity: 'warn', error: error instanceof Error ? error.message : String(error) });
    return undefined;
  }
};

const withSyntheticStrikeout = (diag: TravelOfferDiag, rawData: unknown): TravelOfferDiag => {
  const syntheticStrikeout = parseSyntheticStrikeout(isRecord(rawData) ? rawData.syntheticStrikeout : undefined);
  return syntheticStrikeout ? { ...diag, syntheticStrikeout } : diag;
};

/**
 * A parsed blob is only worth returning if it carries a section the panel can draw. Every field is
 * optional since TRAV-3970, so without this an unrelated JSON object would validate into an empty
 * diagnostic and render a blank panel rather than nothing at all.
 */
const hasRenderableSection = (diag: TravelOfferDiag): boolean => Boolean(diag.margin || diag.syntheticStrikeout);

const sanitizeTravelOfferDiagInput = (data: unknown): unknown => {
  if (!isRecord(data)) {
    return data;
  }

  // `syntheticStrikeout` is parsed separately (see above), so it never reaches the root schema.
  const { syntheticStrikeout: _syntheticStrikeout, ...withoutSynthetic } = data;
  const normalizedOtaUrls = normalizeOtaUrls(data.otaUrls);

  if (normalizedOtaUrls === undefined) {
    const { otaUrls: _otaUrls, ...rest } = withoutSynthetic;
    return rest;
  }

  return {
    ...withoutSynthetic,
    otaUrls: normalizedOtaUrls,
  };
};

const normalizeParsedTravelOfferDiag = (diag: TravelOfferDiag): TravelOfferDiag => {
  const normalizedOtaUrls = normalizeOtaUrls(diag.otaUrls);

  if (normalizedOtaUrls === undefined) {
    const { otaUrls: _otaUrls, ...rest } = diag;
    return rest;
  }

  return {
    ...diag,
    otaUrls: normalizedOtaUrls,
  };
};

export const validateTravelOfferDiag = (data: unknown): TravelOfferDiag | null => {
  if (!data) return null;

  const sanitizedData = sanitizeTravelOfferDiagInput(data);

  try {
    const parsed = withSyntheticStrikeout(
      normalizeParsedTravelOfferDiag(travelOfferDiagSchema.validateSync(sanitizedData, { abortEarly: false, strict: false }) as TravelOfferDiag),
      data,
    );
    return hasRenderableSection(parsed) ? parsed : null;
  } catch (error) {
    addError('validateTravelOfferDiag', { error });
    return null;
  }
};

const isLikelyTravelOfferDiagRoot = (data: unknown): data is TravelOfferDiag => {
  if (!isRecord(data)) return false;
  const record = data as Record<string, unknown>;
  const margin = record.margin;
  if (!isRecord(margin)) return false;
  const m = margin as Record<string, unknown>;
  return (
    typeof record.currency === 'string' &&
    typeof record.eligibilityReason === 'string' &&
    typeof m.markup === 'string' &&
    typeof m.markupNightly === 'string' &&
    typeof m.spread === 'string'
  );
};

/** A blob whose only section is the synthetic strikeout — no margin decision ran for this hotel. */
const isLikelySyntheticStrikeoutOnlyRoot = (data: unknown): boolean => {
  if (!isRecord(data)) return false;
  const syntheticStrikeout = (data as Record<string, unknown>).syntheticStrikeout;
  return isRecord(syntheticStrikeout) && typeof syntheticStrikeout.applied === 'boolean';
};

export const travelOfferDiagFromApiJson = (data: unknown): TravelOfferDiag | null => {
  const sanitizedData = sanitizeTravelOfferDiagInput(data);

  if (data) {
    try {
      const parsed = withSyntheticStrikeout(
        normalizeParsedTravelOfferDiag(travelOfferDiagSchema.validateSync(sanitizedData, { abortEarly: false, strict: false }) as TravelOfferDiag),
        data,
      );
      if (hasRenderableSection(parsed)) {
        return parsed;
      }
    } catch (error) {
      addAction('travelOfferDiagValidationFailed', { severity: 'warn', error: error instanceof Error ? error.message : String(error) });
    }
  }
  if (isLikelyTravelOfferDiagRoot(sanitizedData) || isLikelySyntheticStrikeoutOnlyRoot(data)) {
    // The same renderability floor as the main parse. `isLikelySyntheticStrikeoutOnlyRoot` only checks
    // that `applied` is a boolean, so a section that fails its own schema on some *other* field — a
    // missing `discountPercent`, say — would otherwise reach here, get dropped a second time, and be
    // returned as a diagnostic with no sections at all. `rawDiag` callers would then lazy-load the panel
    // and draw an empty container instead of nothing.
    const fallback = withSyntheticStrikeout(isRecord(sanitizedData) ? (sanitizedData as TravelOfferDiag) : ({} as TravelOfferDiag), data);
    return hasRenderableSection(fallback) ? fallback : null;
  }
  return null;
};
